<template>
  <span v-if="hasMultipleSources || maxWidthToAutosize !== undefined">
    <picture>
      <source
        v-for="(source, index) in cdnSources"
        :key="`${source.url}-${index}`"
        :srcset="source.cdnSrcset"
        :media="source.media"
        :sizes="source.sizes"
      />
      <img
        :src="imgSrc"
        :alt="alt"
        :height="getSourceHeight(imgSrc)"
        :width="getSourceWidth(imgSrc)"
        :srcset="imgSrcset"
        class="adn-image__img-in-picture"
        :loading="loading"
      />
    </picture>
  </span>
  <img
    v-else
    :src="imgSrc"
    :srcset="imgSrcset"
    :height="getSourceHeight(cdnSources[0].url)"
    :width="getSourceWidth(cdnSources[0].url)"
    :alt="alt"
    :loading="loading"
    data-test-img
  />
</template>

<script>
import { getConfig } from '@b2ag/micro-front-tools'
const DPR_RETINA = 'dpr_2.0'
// source cloudinary: prefix : https://res.cloudinary.com/<bucketname>/<media (ici image)>/<stockage (fetch ou upload)>/<?options de transformation>/<chemin du fichier>
const CLOUDINARY_SOURCE =
  /^(https:\/\/res\.cloudinary\.com\/[a-zA-Z0-9-]+\/image\/(?:fetch|upload)\/)((?:(?:[a-z]+_[^/,]+),?\/?)+\/)?((?:v\d+\/?)?(?:.+))/

export default {
  name: 'AdnImage',
  props: {
    source: {
      type: [String, Array],
    },
    alt: {
      type: String,
    },
    cloudinaryOptions: {
      type: String,
      default: 'w_auto,q_auto,f_auto,c_pad',
    },
    breakpoints: {
      type: Array,
      default: () => [
        { min: 360, max: 480, quality: 'q_auto:eco' },
        { min: 480, max: 576, quality: 'q_auto:eco' },
        { min: 576, max: 768, quality: 'q_auto' },
        { min: 768, max: 992, quality: 'q_auto' },
        { min: 992, quality: 'q_auto' },
      ],
    },
    maxWidthToAutosize: {
      type: Number,
    },
    additionalOptions: {
      type: String,
    },
    loading: {
      type: String,
      default: 'lazy',
    },
  },
  computed: {
    hasMultipleSources() {
      return Array.isArray(this.source)
    },
    cdnSingleSource() {
      if (this.maxWidthToAutosize === undefined) {
        return [
          this.updateCDNUrl({
            url: this.source,
            additionalOptions: this.additionalOptions,
          }),
        ]
      }
      return this.getSourcesByMaxWidth(this.source, this.maxWidthToAutosize)
    },
    cdnSources() {
      if (!this.hasMultipleSources) {
        return this.cdnSingleSource
      }
      return this.source.map((source) => this.updateCDNUrl(source))
    },
    imgSrc() {
      return this.cdnSources[this.cdnSources.length - 1] && this.cdnSources[this.cdnSources.length - 1].cdnUrl
    },
    imgSrcset() {
      return this.cdnSources[this.cdnSources.length - 1] && this.cdnSources[this.cdnSources.length - 1].srcset
    },
  },
  methods: {
    cloudinaryFetchUrlWithOptions(url, additionalOptions, withRetina = false) {
      let image = url
      // Asset image
      if (!url.startsWith('http')) {
        const prefix = getConfig('BASE_URL').includes('localhost') ? 'http' : 'https'
        image = `${prefix}://${getConfig('BASE_URL')}${`/${image}`.replace(/(\/){2,}/g, '/')}`
      }
      if (!getConfig('CLOUDINARY_BUCKET_NAME') || getConfig('BASE_URL').includes('localhost')) {
        return image
      }
      let computedOptions = additionalOptions
      if (!additionalOptions) {
        computedOptions = this.cloudinaryOptions
      }
      if (withRetina) {
        const optionArray = computedOptions ? computedOptions.split(',') : []
        optionArray.push(DPR_RETINA)
        computedOptions = optionArray.join(',')
      }
      if (computedOptions?.[0] === ',') {
        computedOptions = computedOptions.substring(1)
      }
      // Traiter les sources cloudinary
      const cloudinaryMatch = CLOUDINARY_SOURCE.exec(image)

      if (cloudinaryMatch) {
        image = this.handleCloudinarySource(cloudinaryMatch, computedOptions)
        return image
      }

      // Retrieve asset with cloudinary
      const prefix = `https://res.cloudinary.com/${getConfig('CLOUDINARY_BUCKET_NAME')}/image/fetch/${computedOptions}/`
      // escape '?' char for cloudinary
      image = image.replace(/\?/g, '%3F')
      return `${prefix}${image}`
    },
    // Le 1er parametre est issu de CLOUDINARY_SOURCE.exec => [0] : toute la chaine qui matche, ici l url entier
    //                           [1] : prefixe, ici https://res.cloudinary.com/aladinfarm/image/<fetch ou upload>/
    //                           [2] : options, exemple w_400,h_350,q_auto,f_auto,c_fill/ , si il n y a pas d option -> undefined
    //                           [3] : le reste de l'url, exemple https://idfmarketplace.blob.core.windows.net/img-products/CERIENCE_FOURRAGE_SAC.jpg ou
    //                                 v1647364697/products/FOURNISSEURS/FOURRAGERES/trefle_incarnat_uk5lnu.jpg

    handleCloudinarySource([url, prefix, originalOptions], computedOptions) {
      let result = url
      if (!computedOptions) return result
      // si l'url n'a pas d'options
      if (!originalOptions) {
        // inserer les options definies apres le prefixe
        result = url.replace(new RegExp(prefix), `${prefix}${computedOptions}/`)
      } else {
        // sinon remplacer les options par celles de computedOptions pour les options communes
        const optionArray = computedOptions.split(',')
        let mergedOptions = originalOptions.slice(0, -1)
        // if the original options have multiple transformations we dont handle
        if (mergedOptions.includes('/')) return result

        optionArray.forEach((option) => {
          const optionPrefix = `${option.split('_')[0]}_`
          // si l option n existe pas dans les options originales on l ajoute
          if (!mergedOptions.includes(optionPrefix)) {
            mergedOptions += `,${option}`
          } else {
            // sinon on remplace par l'option définie
            const optionRegexp = new RegExp(`\\b(?:${optionPrefix}[\\w:]+)`)
            mergedOptions = mergedOptions.replace(optionRegexp, option)
          }
        })
        result = url.replace(new RegExp(originalOptions), `${mergedOptions}/`)
      }
      return result
    },
    adjustUrlWithCDN(url, additionalOptions = '', isSourceSetUrl = false) {
      if (!url) {
        return ''
      }
      // Base64 encoded image
      if (url.startsWith('data:image')) {
        return url
      }
      // DEV NOTE: we want to write this if at this very place because we want to keep the closest
      // behavior possible between production and dev environments
      if (getConfig('ENVIRONMENT_NAME') === 'DEV') {
        return url
      }

      // Hosted anywhere but needs to be fetched through CDN/Cloudinary
      const normalUrl = this.cloudinaryFetchUrlWithOptions(url, additionalOptions)

      if (isSourceSetUrl) {
        const retinaUrl = this.cloudinaryFetchUrlWithOptions(url, additionalOptions, true)
        return `${normalUrl} 1x, ${retinaUrl} 2x`
      }
      return normalUrl
    },
    createAdditionalOptions(width, quality, options = {}) {
      const mergedOptions = { width, quality, format: 'f_auto', fill: 'c_pad', ...options }
      return `w_${mergedOptions.width},${mergedOptions.quality},${mergedOptions.format},${mergedOptions.fill}`
    },
    getSourceMedia(url, min, max, quality, options) {
      const media = max ? `(min-width:${min}px) and (max-width:${max}px)` : `(min-width:${min}px)`
      return {
        url,
        media,
        additionalOptions: this.createAdditionalOptions(max || 'auto', quality, options),
      }
    },
    getSourcesByMaxWidth(url, maxWidth) {
      const breakpoints = this.breakpoints.filter((breakpoint) => !maxWidth || breakpoint.max < maxWidth)
      const sources = breakpoints.map((breakpoint) =>
        this.getSourceMedia(url, breakpoint.min, breakpoint.max, breakpoint.quality, breakpoint.options),
      )
      // Normal case max width is defined and in our breakpoints
      if (maxWidth && breakpoints.length > 0) {
        sources.push({
          url,
          media: `(min-width:${breakpoints[breakpoints.length - 1].max}px)`,
          additionalOptions: this.createAdditionalOptions(
            maxWidth,
            'q_auto',
            breakpoints[breakpoints.length - 1].options,
          ),
        })
      }

      // In case the max width is smaller than our smallest breakpoint (480px)
      if (breakpoints.length === 0 || maxWidth < breakpoints[0].min) {
        const options = breakpoints.length === 0 ? {} : breakpoints[0].options
        sources.push({
          url,
          additionalOptions: this.createAdditionalOptions(maxWidth, 'q_auto', options),
        })
      } else {
        sources.push({
          url,
          additionalOptions: this.createAdditionalOptions(
            this.breakpoints[0].min,
            this.breakpoints[0].quality,
            this.breakpoints[0].options,
          ),
        })
      }
      return sources.map((source) => this.updateCDNUrl(source))
    },
    getSourceWidth(source) {
      // Check is base64 image
      if (source && source.startsWith('data:')) {
        // Try to parse options
        const optionRegex = /.*w_(\d+),?/gm
        const optionResult = optionRegex.exec(this.additionalOptions)
        return optionResult && optionResult.length === 2 ? optionResult[1] : 'auto'
      }
      const regex = /\/fetch\/.*w_(\d+)[,/]/gm
      const result = regex.exec(source)
      return result && result.length === 2 ? result[1] : 'auto'
    },
    getSourceHeight(source) {
      // Check is base64 image
      if (source && source.startsWith('data:')) {
        // Try to parse options
        const optionRegex = /.*h_(\d+),?/gm
        const optionResult = optionRegex.exec(this.additionalOptions)
        return optionResult && optionResult.length === 2 ? optionResult[1] : 'auto'
      }
      const regex = /\/fetch\/.*h_(\d+)[,/]/gm
      const result = regex.exec(source)
      return result && result.length === 2 ? result[1] : 'auto'
    },
    updateCDNUrl(source) {
      // if (!source.url) {
      //   console.error('The provided source does not have an url property')
      // }
      return {
        ...source,
        cdnUrl: this.adjustUrlWithCDN(source.url, source.additionalOptions, false),
        cdnSrcset: this.adjustUrlWithCDN(source.url, source.additionalOptions, true),
      }
    },
  },
}
</script>
<style scoped lang="scss">
.adn-image__img-in-picture {
  width: 100%;
  height: 100%;
}
</style>
